import { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PullToRefreshProps {
    children: React.ReactNode;
    onRefresh?: () => Promise<void> | void;
    threshold?: number;
    disabled?: boolean;
}

export default function PullToRefresh({
    children,
    onRefresh,
    threshold = 80,
    disabled = false
}: PullToRefreshProps) {
    const navigate = useNavigate();
    const [isPulling, setIsPulling] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [pullDistance, setPullDistance] = useState(0);
    const startY = useRef(0);
    const currentY = useMotionValue(0);
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const isPWA = typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches;

    // Só ativar no mobile/PWA
    const shouldEnable = (isMobile || isPWA) && !disabled;

    const handleRefresh = async () => {
        if (!onRefresh) {
            // Refresh padrão: recarregar a página
            window.location.reload();
            return;
        }

        setIsRefreshing(true);
        try {
            await onRefresh();
        } catch (error) {
            console.error('Erro ao atualizar:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (!shouldEnable) return;

        const handleTouchStart = (e: TouchEvent) => {
            // Só ativar se estiver no topo da página
            if (window.scrollY <= 5) {
                startY.current = e.touches[0].clientY;
                setIsPulling(true);
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (!isPulling) return;

            const currentTouchY = e.touches[0].clientY;
            const distance = currentTouchY - startY.current;

            if (distance > 0 && window.scrollY <= 5) {
                e.preventDefault();
                const newDistance = Math.min(distance * 0.5, threshold * 1.5);
                setPullDistance(newDistance);
                currentY.set(newDistance);
            }
        };

        const handleTouchEnd = () => {
            if (!isPulling) return;

            if (pullDistance >= threshold && !isRefreshing) {
                handleRefresh();
            }

            setIsPulling(false);
            setPullDistance(0);
            currentY.set(0);
        };

        if (shouldEnable) {
            document.addEventListener('touchstart', handleTouchStart, { passive: false });
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleTouchEnd);
        }

        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isPulling, pullDistance, threshold, isRefreshing, shouldEnable, onRefresh, navigate]);

    const rotate = useTransform(currentY, [0, threshold], [0, 360]);
    const scale = useTransform(currentY, [0, threshold], [0.8, 1.2]);
    const opacity = useTransform(currentY, [0, threshold * 0.5, threshold], [0, 0.5, 1]);

    return (
        <div className="relative">
            <AnimatePresence>
                {(isPulling || isRefreshing) && shouldEnable && (
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{
                            opacity: isRefreshing ? 1 : opacity.get(),
                            y: isRefreshing ? 0 : -50 + pullDistance * 0.3
                        }}
                        exit={{ opacity: 0, y: -50 }}
                        className="fixed top-0 left-0 right-0 z-[200] flex items-center justify-center pt-4 pointer-events-none"
                        style={{
                            height: isRefreshing ? 60 : Math.max(0, pullDistance * 0.3)
                        }}
                    >
                        <motion.div
                            animate={isRefreshing ? { rotate: 360 } : {}}
                            transition={isRefreshing ? {
                                rotate: {
                                    repeat: Infinity,
                                    duration: 1,
                                    ease: "linear"
                                }
                            } : {}}
                            style={{
                                rotate: isRefreshing ? undefined : rotate,
                                scale: isRefreshing ? 1 : scale,
                                opacity: isRefreshing ? 1 : opacity
                            }}
                            className="bg-orange-500/20 backdrop-blur-md rounded-full p-3 border border-orange-500/30"
                        >
                            <RefreshCw size={24} className="text-orange-500" />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div
                style={{
                    y: isRefreshing ? 60 : Math.max(0, pullDistance * 0.3)
                }}
                transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                }}
            >
                {children}
            </motion.div>
        </div>
    );
}

